import React from 'react';
import type { Quest } from '../../../../shared/NPCData';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import { ItemIcon } from '../../components/ItemIcon';
import { TomeRewardLine } from '../../primitives/tome';
import { Tooltip, createItemTooltip } from '../../components/Tooltip';
import { useItemTooltipViewer } from '../../hooks/useItemTooltipViewer';

/** One reward item row; hovering it shows the shared item tooltip (plan 23 row 27). */
const RewardItem: React.FC<{ id: string; name: string; quantity?: number }> = ({ id, name, quantity }) => {
  const viewer = useItemTooltipViewer();
  const known = ITEM_DATABASE[id] !== undefined;
  const row = <span className="npc-reward-item" data-reward-item-id={id} tabIndex={known ? 0 : undefined}>
    {known && <span aria-hidden="true" data-item-art-id={id}><ItemIcon itemId={id} size={32} /></span>}
    <span>{name}{quantity !== undefined && <> <strong>×{quantity}</strong></>}</span>
  </span>;
  return known ? <Tooltip inline data={createItemTooltip({ id, name }, viewer)}>{row}</Tooltip> : row;
};

/** UI-only IDs come from canonical quest definitions, never guessed from display labels. */
export const NpcQuestRewards: React.FC<{ quest: Quest; label?: string }> = ({ quest, label = 'Rewards' }) => {
  const parts: React.ReactNode[] = [`${quest.rewardXP.toLocaleString()} XP`, `${quest.rewardGold.toLocaleString()} Gold`];
  if (quest.rewardAbility?.hidden) {
    // A surprise (rewards.ability_hidden): no id or name reaches the page until turn-in.
    parts.push(<span data-reward-ability-hidden="true" title="Learn it to find out">New ability: ?</span>);
  } else if (quest.rewardAbility) {
    parts.push(<span data-reward-ability-id={quest.rewardAbility.id}>New ability: {quest.rewardAbility.name}</span>);
  }
  const items = quest.rewardItemDetails !== undefined ? <ul className="npc-reward-items">
    {quest.rewardItemDetails.map((item, index) => <li key={`${item.id}-${index}`}>
      <RewardItem id={item.id} name={item.name} quantity={item.quantity} />
    </li>)}
  </ul> : quest.rewardItems.length > 0 ? <ul className="npc-reward-items">
    {quest.rewardItems.map((label, index) => <li key={index}>
      <RewardItem id={label} name={ITEM_DATABASE[label]?.name ?? label} />
    </li>)}
  </ul> : null;
  return <TomeRewardLine className="npc-quest-rewards" label={label}
    aria-label={label === 'Rewards' ? 'Quest rewards' : label} parts={parts} items={items} />;
};
